/**
 * Display helpers for timestamps. The backend ALWAYS stores UTC (ISO 8601); these render an
 * instant in the user's chosen IANA timezone (default UTC) — timezone is a display concern only.
 */

/** The user's timezone, defaulted. Pass `user.timezone` (which is undefined until they pick one). */
export const resolveTimezone = (tz?: string | null): string => (tz && tz.trim()) || 'UTC';

/**
 * Full activity timestamp: e.g. "29 Jun 2026, 14:03:51 GMT". Date + hh:mm:ss + region (the short
 * timezone name), rendered in `tz`. Returns '' for a null/unparseable instant.
 */
export function formatInstant(iso: string | null | undefined, tz?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const timeZone = resolveTimezone(tz);
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      timeZoneName: 'short',
    }).format(d);
  } catch {
    // Invalid timezone id → fall back to UTC so we never throw in the render path.
    return new Intl.DateTimeFormat(undefined, {
      timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZoneName: 'short',
    }).format(d);
  }
}

/** Just the short timezone label for an instant (e.g. "GMT", "PDT", "GMT+8") — used as a hint. */
export function timezoneLabel(tz?: string | null, at: Date = new Date(0)): string {
  const timeZone = resolveTimezone(tz);
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'short' }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value || timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * The list of IANA timezones for the settings picker. Uses the runtime's full list when available
 * (modern browsers), otherwise a small curated fallback so the picker is never empty.
 */
export function listTimezones(): string[] {
  const supported = (Intl as any).supportedValuesOf;
  if (typeof supported === 'function') {
    try {
      const zones = supported('timeZone') as string[];
      // The runtime list uses canonical ids and omits the plain "UTC" alias — ensure it's offered first.
      return zones.includes('UTC') ? zones : ['UTC', ...zones];
    } catch { /* fall through */ }
  }
  return [
    'UTC', 'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
    'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
    'Africa/Johannesburg', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Shanghai',
    'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland',
  ];
}

/** The browser's best guess at the user's timezone, for a sensible default in the picker. */
export function guessTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}
